import { DefaultInventoryModel, Inventory, InventorySetting } from 'containers/Limitations/Inventory';
import { defaultItemSetting } from 'containers/Limitations/SelectItemComponent';
import { useCallback, useMemo, useState } from 'react';
import i18n from 'i18n';
import { DefaultSelectComponentModel, SelectComponent } from 'containers/Limitations/SelectComponent';
import { SelectOptions } from 'components/commonType';
import { withFormikField } from 'hoc/withFormikField';
import { DEFAULT_INVENTORY } from 'containers/Limitations/LimitationSetting/LimitationSettingData';
import { flattenOptions } from 'utils/SelectOptionsUtils';
import { Form, Row } from 'react-bootstrap';
import Tags from 'components/Tags/Tags';
import Dropdowns from 'components/Dropdowns/Dropdowns';
import classnames from 'classnames/bind';
import Tips from 'components/Tips/Tips';
import styles from './productCategorySelectField.module.scss';
import { validateEmpty } from 'utils/ValidateUtils';
import { belongsToInventory, nextValueAfterSelect, pathToLabel } from './ProductCategoryPathUtils';

const cx = classnames.bind(styles);

export const MAX_CATEGORIES = 20;

export type ProductCategoriesSelectFieldProps = {
  inventorySettings: InventorySetting[];
  value: string[];
  error?: string;
  onChange: (newValue: string[]) => void;
};

export const ProductCategoriesSelectField: React.FC<ProductCategoriesSelectFieldProps> = ({
  inventorySettings,
  value,
  error,
  onChange
}) => {

  const [inventorySelected, setInventorySelected] = useState<string>(
    inventorySettings[0] ? inventorySettings[0].name : DEFAULT_INVENTORY
  );

  const inventorySettingModel = useMemo(() => {
    return new DefaultInventoryModel(
      inventorySelected,
      inventorySettings,
      {},
      'productCategorySelectField.titles.productCategory',
      setInventorySelected
    );
  }, [inventorySelected, inventorySettings]);

  const selectComponents = useMemo(() => {
    return inventorySettings.filter(setting => setting.name !== DEFAULT_INVENTORY).map((inventorySetting) => {
      const {
        name,
        cb
      } = inventorySetting;

      const optionsOfCategory = cb() as SelectOptions[];
      const selectedValueOfCategory = value.filter((item) => belongsToInventory(item, name));
      const selectedOptionsOfCategory = flattenOptions(optionsOfCategory).filter(option => selectedValueOfCategory.includes(option.value.toString()));
      const resolvedPathsOfCategory = selectedOptionsOfCategory.map(option => option.value.toString());

      // SelectComponentModel.onItemChange 已經把新點選的節點原樣加入，並在點的是父層時
      // 移除其下已選的子項，所以這裡直接沿用該層級，不再攤平成葉節點。
      const onSelect = (newValues: SelectOptions[]) => {
        onChange(
          nextValueAfterSelect(
            value,
            resolvedPathsOfCategory,
            newValues.map(option => option.value.toString())
          )
        );
      };

      let model = new DefaultSelectComponentModel(
        name,
        false,
        defaultItemSetting(),
        i18n.t<string>('productCategorySelectField.placeholder'),
        onSelect,
        [],
        selectedOptionsOfCategory,
        false
      );
      return <SelectComponent key={name} model={model} />;
    });
  }, [inventorySettings, value, onChange]);

  const deleteAllCategories = useCallback(() => {
    onChange([]);
  }, [onChange]);

  const tagsTitle = useMemo(() => i18n.t<string>('productCategorySelectField.titles.productCategory'), []);

  const tagsValueWithi18n = useMemo(() => {
    return value.map((path) => ({
      value: path,
      label: pathToLabel(path)
    }));
  }, [value]);

  const onTagChanged = useCallback(
    (newTags: (SelectOptions | string)[]) => {
      onChange(
        newTags.map((tag) => (typeof tag === 'string' ? tag : tag.value.toString()))
      );
    },
    [onChange]
  );

  const tagsClassName = cx(styles.tags, {
    hasError: error !== undefined
  });

  const selectedCountClassName = cx(styles.selectedCount, {
    exceedLimit: value.length > MAX_CATEGORIES
  });

  return (
    <div className={styles.selectContainer}>
      <div style={{ flexShrink: 0 }}>
        <Inventory width={424} model={inventorySettingModel}>
          {selectComponents}
        </Inventory>
      </div>
      <div className={styles.tagsContainer}>
        <Form.Group as={Row} controlId={tagsTitle}>
          <div className={styles.tagsMain}>
            <div className={styles.tagsHeader}>
              <Form.Label>{i18n.t<string>(tagsTitle)}</Form.Label>
              <span className={selectedCountClassName}>
                {i18n.t<string>('productCategorySelectField.labels.selectedCount', {
                  selected: value.length,
                  max: MAX_CATEGORIES
                })}
              </span>
            </div>
            <Tags
              value={tagsValueWithi18n}
              operate={'include'}
              onChange={onTagChanged}
              className={tagsClassName}
            />
          </div>
          <Dropdowns
            type={'productCategory'}
            deleteLimitation={deleteAllCategories}
          />
        </Form.Group>
        <Tips>{error}</Tips>
      </div>
    </div>
  );
};

export type FormikProductCategoriesSelectFieldProps = {
  name: string;
  inventorySettings: InventorySetting[];
  validate?: (categories: string[]) => string | undefined;
};

export const FormikProductCategoriesSelectField = withFormikField<FormikProductCategoriesSelectFieldProps>(ProductCategoriesSelectField);

const validateProductCategories = (categories: string[]) => {
  const error = validateEmpty(categories);
  if (error) {
    return error;
  }
  if (categories.length > MAX_CATEGORIES) {
    return i18n.t<string>('productCategorySelectField.errors.exceedLimit', { max: MAX_CATEGORIES });
  }
  return undefined;
};

export const renderFormikProductCategoriesSelectField = (
  name: string,
  inventorySettings: InventorySetting[],
  validate: (
    categories: string[]
  ) => string | undefined = validateProductCategories
) => {
  return (
    <FormikProductCategoriesSelectField
      name={name}
      inventorySettings={inventorySettings}
      validate={validate}
    />
  );
};
